// src/data/shelterNotes.ts
// ------------------------
// Repository for the `shelter_notes` table — internal per-shelter notes + the
// "no requests to approve" flag, shown in the request detail panel. Typed via
// getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type ShelterNotesRow = { notes: string | null; no_approvals: boolean | null };
type ShelterNotesInsert = Database["public"]["Tables"]["shelter_notes"]["Insert"];

// The notes row for a shelter, looked up by code (preferred) or name. Null if
// none / error / no client.
export async function getShelterNotes(
  field: "shelter_code" | "shelter_name",
  value: string
): Promise<ShelterNotesRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("shelter_notes").select("*").eq(field, value).maybeSingle();
  if (error) {
    logger.warn("getShelterNotes failed:", error.message);
    return null;
  }
  return (data as unknown as ShelterNotesRow) ?? null;
}

// Upsert a shelter's notes row (keyed on the shelter_code unique constraint).
export async function upsertShelterNotes(
  row: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("shelter_notes")
    .upsert(row as ShelterNotesInsert, { onConflict: "shelter_code" });
  if (error) logger.warn("upsertShelterNotes failed:", error.message);
  return { error };
}
